import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, MapPin, UserPlus, Users, ArrowRight } from "lucide-react";
import { EventsGridSkeleton, EventsListSkeleton } from "@/components/Skeletons";

/**
 * interface EventItem {
 *   id: string;
 *   title: string;
 *   date: string;
 *   time: string;
 *   location: string;
 *   type: "Collective" | "Service" | "Special";
 *   description: string;
 *   registration_enabled?: boolean;
 * }
 * interface EventsCarouselProps {
 *   events: EventItem[];
 *   isLoading?: boolean;
 * }
 */
export default function EventsCarousel({ events = [], isLoading = false }) {
  // If no events and not loading, don't render the section at all to save space
  if (!isLoading && events.length === 0) return null;

  return (
    <section data-testid="events-carousel-section" className="py-20 md:py-32 bg-white">
      <div className="container-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-gold" />
              <span className="text-navy text-sm font-heading uppercase tracking-widest font-bold">
                Apostolic Unity
              </span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl text-navy mb-2">
              Upcoming Gatherings
            </h2>
            <p className="text-gray-500 text-sm font-medium">Join us in the Center or in a home-based Collective</p>
          </div>
          
          <Link to="/live-events" className="hidden sm:block shrink-0">
            <Button variant="outline" className="border-gold/40 text-navy hover:bg-gold/10 hover:border-gold/60 shadow-sm transition-all duration-300 rounded-full px-8 font-bold uppercase tracking-wider text-xs h-10">
              View All Calendar
            </Button>
          </Link>
        </div>

        {/* Content */}
        {isLoading ? (
          <>
            <div className="hidden md:block"><EventsGridSkeleton count={3} /></div>
            <div className="md:hidden"><EventsListSkeleton count={4} /></div>
          </>
        ) : (
          <>
            {/* Desktop View: Grid */}
            <div className="hidden md:grid md:grid-cols-3 gap-8">
              {events.slice(0, 3).map((event, i) => (
                <Link to="/live-events" key={event.id} className="block group h-full">
                  <Card className="h-full bg-bone/30 border-none ring-1 ring-bone-dark shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-500 rounded-2xl overflow-hidden flex flex-col">
                    <div className="h-2 w-full bg-gradient-to-r from-gold/40 via-gold to-gold/40" />
                    
                    <CardContent className="p-8 flex-1 flex flex-col">
                      <div className="flex items-center justify-between gap-2 mb-6">
                        <div className="bg-navy text-bone rounded-xl px-4 py-2 text-center min-w-[70px] shadow-sm border border-gold/20">
                          <p className="text-[10px] uppercase text-gold leading-tight tracking-widest font-bold mb-0.5">
                            {new Date(event.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}
                          </p>
                          <p className="text-2xl font-bold font-heading leading-none">
                            {new Date(event.date + "T12:00:00").getDate()}
                          </p>
                        </div>
                        
                        {event.registration_enabled && (
                          <Badge className="bg-gold/15 text-gold border border-gold/30 hover:bg-gold/20 px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                            <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Register
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="font-heading text-2xl text-navy mb-3 group-hover:text-gold transition-colors duration-300">
                        {event.title}
                      </h3>
                      
                      <div className="flex flex-col gap-2 mb-6">
                        {event.time && (
                          <p className="text-gray-500 text-sm flex items-center gap-2 font-medium">
                            <Clock className="h-4 w-4 text-gold/80" /> {event.time}
                          </p>
                        )}
                        {event.location && (
                          <p className="text-gray-500 text-sm flex items-center gap-2 font-medium truncate">
                            <MapPin className="h-4 w-4 text-gold/80 shrink-0" /> {event.location.split(',')[0]}
                          </p>
                        )}
                      </div>
                      
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-6 flex-1">
                        {event.description}
                      </p>
                      
                      <div className="mt-auto pt-6 border-t border-bone-dark flex items-center justify-between">
                        <span className="text-xs font-bold text-navy uppercase tracking-wider">View Details</span>
                        <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold group-hover:text-navy transition-colors duration-300">
                          <ArrowRight className="h-4 w-4 text-gold group-hover:text-navy" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Mobile View: Horizontal Swipeable List */}
            <div className="md:hidden -mx-4 px-4 overflow-x-auto pb-8 pt-2 snap-x snap-mandatory flex gap-4 scrollbar-hide">
              {events.slice(0, 4).map((event) => (
                <Link to="/live-events" key={event.id} className="block snap-center shrink-0 w-[85vw] max-w-[320px]">
                  <Card className="h-full bg-bone/30 border-none ring-1 ring-bone-dark shadow-sm active:bg-bone-dark transition-all duration-300 rounded-2xl overflow-hidden flex flex-col">
                    <div className="h-1.5 w-full bg-gradient-to-r from-gold/40 via-gold to-gold/40" />
                    
                    <CardContent className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-navy text-bone rounded-lg px-3 py-1.5 text-center min-w-[60px] shadow-sm border border-gold/20">
                          <p className="text-[9px] uppercase text-gold leading-tight tracking-widest font-bold mb-0.5">
                            {new Date(event.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}
                          </p>
                          <p className="text-xl font-bold font-heading leading-none">
                            {new Date(event.date + "T12:00:00").getDate()}
                          </p>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-lg text-navy truncate font-bold">{event.title}</h3>
                          {event.time && (
                            <p className="text-gray-500 text-xs flex items-center gap-1.5 mt-1 font-medium">
                              <Clock className="h-3.5 w-3.5 text-gold/80 shrink-0" /> <span className="truncate">{event.time}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-5 flex-1">
                        {event.description}
                      </p>
                      
                      <div className="mt-auto flex items-center justify-between text-gold">
                        {event.registration_enabled ? (
                          <Badge className="bg-gold/15 text-gold border border-gold/30 px-2.5 py-0.5 rounded-md text-[10px] font-bold">
                            <UserPlus className="h-3 w-3 mr-1" /> Register
                          </Badge>
                        ) : (
                          <span className="text-[11px] font-bold uppercase tracking-widest">Read More</span>
                        )}
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              
              {/* "View All" Card for mobile carousel */}
              {events.length > 4 && (
                <Link to="/live-events" className="block snap-center shrink-0 w-[40vw] max-w-[160px]">
                  <Card className="h-full bg-navy border-none shadow-md rounded-2xl flex items-center justify-center p-6 text-center group">
                    <div>
                      <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4 group-active:scale-95 transition-transform">
                        <ArrowRight className="h-6 w-6 text-gold" />
                      </div>
                      <p className="text-bone font-heading text-lg">View All Events</p>
                    </div>
                  </Card>
                </Link>
              )}
            </div>

            {/* Mobile bottom CTA */}
            <div className="mt-2 text-center sm:hidden">
              <Link to="/live-events">
                <Button variant="outline" className="w-full border-gold/40 text-navy bg-white hover:bg-gold/10 shadow-sm rounded-full h-12 font-bold uppercase tracking-wider text-xs">
                  View Full Calendar
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}